import { useParams } from "react-router";
import { Branch } from "@/services/backend/company/types/branch";
import {
  useBranchesBranchShowQuery,
  usePutBranchMutation,
} from "@/services/backend/company/api/branch";
import { Card, CardContent } from "@/shared/components/ui/card";
import t from "@/lang/lang";
import { TextInput } from "@/shared/components/form/text-input";
import {
  inputValidationErrorMessages,
  InputValidationErrors,
} from "@/shared/components/errors/input-error-messages";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { useState } from "react";
import { useDebouncedMutation } from "@/shared/lib/debounce/debounce";
import { CardSkeleton } from "@/shared/components/card-skeleton";
import { AddressCard } from "@/services/backend/addresses/components/address-card";
import {
  BaseDataPage,
  BaseDataPageContent,
  BaseDataPageHeader,
  BaseDataPageTitle,
} from "@/routes/base-data/_components/layout/base-data-page";
import { PersistenceContextProvider } from "@/shared/lib/persistence-state/provider";
import { PersistenceStateUsingContext } from "@/shared/lib/persistence-state/persistence-state-info";
import { DeleteBranchDialog } from "@/services/backend/company/components/delete-branch-dialog";
import { Label } from "@/shared/components/ui/label";
import { CompanySelect } from "@/services/backend/company/components/company-select";

export function BranchRoute() {
  const { branchId } = useParams();
  const {
    data: branch,
    error,
    isLoading,
  } = useBranchesBranchShowQuery({ id: branchId! });

  return (
    <PersistenceContextProvider>
      <BaseDataPage>
        <BaseDataPageHeader>
          <BaseDataPageTitle>
            {t("Niederlassung")} {branch?.name}
            <div className="flex items-center gap-4">
              <PersistenceStateUsingContext />
              {branch && <DeleteBranchDialog branch={branch} />}
            </div>
          </BaseDataPageTitle>
        </BaseDataPageHeader>
        <BaseDataPageContent>
          {error && <RTKQueryErrorAlert error={error} />}
          {isLoading && <CardSkeleton />}
          {branch && <BranchPage branch={branch} key={branch.id} />}
        </BaseDataPageContent>
      </BaseDataPage>
    </PersistenceContextProvider>
  );
}

function BranchPage({ branch }: { branch: Branch }) {
  const [request, setRequest] = useState(branch);
  const mutation = usePutBranchMutation();
  const { error } = useDebouncedMutation(request, setRequest, mutation, branch);

  return (
    <div className="grid gap-2">
      <BaseData branch={request} onBranchChange={setRequest} error={error} />
      <AddressCard
        address={request.address}
        onAddressChange={(address) => setRequest({ ...request, address })}
      />
    </div>
  );
}

function BaseData({
  branch,
  onBranchChange,
  error,
}: {
  branch: Branch;
  onBranchChange: (branch: Branch) => void;
  error: unknown;
}) {
  return (
    <Card>
      <CardContent className="grid gap-4 md:grid-cols-2">
        <TextInput
          label={t("Name der Niederlassung")}
          field="name"
          text={branch.name}
          onTextChange={(name) => onBranchChange({ ...branch, name })}
          errors={inputValidationErrorMessages(error, "name")}
        />
        <div className="grid gap-1.5">
          <Label>{t("Unternehmen")}</Label>
          <CompanySelect
            companyId={branch.companyId}
            onCompanyIdChange={(companyId) =>
              onBranchChange({ ...branch, companyId })
            }
            disabled
          />
          <InputValidationErrors field="companyId" error={error} />
        </div>
        <TextInput
          label={t("Telefonnummer")}
          field="phone"
          text={branch.phone}
          onTextChange={(phone) => onBranchChange({ ...branch, phone })}
          errors={inputValidationErrorMessages(error, "phone")}
        />
        <TextInput
          label={t("E-Mail")}
          field="email"
          text={branch.email}
          onTextChange={(email) => onBranchChange({ ...branch, email })}
          errors={inputValidationErrorMessages(error, "email")}
        />
        <TextInput
          label={t("Niederlassungsleiter")}
          field="branchManager"
          text={branch.branchManager}
          onTextChange={(branchManager) =>
            onBranchChange({ ...branch, branchManager })
          }
          errors={inputValidationErrorMessages(error, "branchManager")}
        />
      </CardContent>
    </Card>
  );
}
